"use client";

import { cn } from "@ui/lib";
import { Volume2Icon, VolumeXIcon } from "lucide-react";
import { type CSSProperties, useEffect, useRef, useState } from "react";
import {
	UseShowcaseExampleButton,
	UseShowcaseImagesButton,
} from "./ScrollToGeneratorButton";
import type { HeroShowcaseExample } from "./VideoGeneratorHero";

export type ShowcaseTile = {
	id: string;
	title: string;
	description: string;
	/** 悬停时显示的模式标签，已翻译；不传就只显示标题 */
	modeLabel?: string;
	/** square：/ai-music-video-generator 的 1:1 样片（成片跟照片比例走） */
	orientation: "landscape" | "portrait" | "square";
	poster: string;
	videoUrl: string;
	/** 有就在浮层里出回填原图的按钮（预设页用） */
	inputImages?: string[];
	/** 预设页有多条模板时，回填原图前先切到出这条片的模板 */
	templateId?: string;
	/** Seedance 页的完整回填数据；有它时出 Use template 按钮（悬停显示，触屏常显） */
	example?: HeroShowcaseExample;
	/** 成片带音轨：右上角出静音开关，默认静音 */
	hasAudio?: boolean;
	/** 外部参考片等需持续展示来源的卡片 */
	alwaysShowCaption?: boolean;
};

/** 列数 → 网格类。4 列是首页拼贴；3 列给全是横版的 hotel lobby 预设页；1 列是首尾帧页的单条蒙太奇 */
const GRID_CLASSES = {
	1: "mx-auto grid max-w-[1000px] grid-cols-1",
	3: "grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4",
	4: "grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4",
} as const;

/**
 * 手机横滑：三条竖版 9:16 单列竖排要 3 屏多（chuttamalle 页 2026-10-09 实测 2177px），
 * 改成一行左右滑、每条 72% 宽露出下一条的边；sm 起回到 3 列网格。-mx-4 / px-4 抵消 HomeSection 的手机边距
 */
const MOBILE_CAROUSEL_CLASSES =
	"-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:overflow-visible sm:px-0";

/**
 * 一行排开（singleRow）：竖版 9:16 多条时三列两行太大（AI Kiss 页 2026-10-12，一格 400×711），
 * lg 起所有条一行等宽（6 条约 192×341，对齐参考页 imgveo 的一行轮播）；以下横滑，手机每条 44% 宽露出两条半，
 * 平板 30%。-mx-4 / px-4 抵消 HomeSection 的手机边距，scroll-px-4 让吸附对齐到边距内
 */
const SINGLE_ROW_CLASSES =
	"-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 [scrollbar-width:none] sm:-mx-6 sm:scroll-px-6 sm:gap-4 sm:px-6 lg:mx-0 lg:grid lg:auto-cols-fr lg:grid-flow-col lg:overflow-visible lg:px-0";

/**
 * 一行等高（equalHeightRow）：sm 起一行排开，每条按画幅比例分宽。手机上横滑的话横版只有竖版
 * 三分之一高、下面空一大块，所以改成上下排：横版满宽，竖版六成宽居中
 */
const EQUAL_HEIGHT_ROW_CLASSES =
	"flex flex-col items-center gap-3 sm:flex-row sm:items-stretch sm:gap-4";

/**
 * 首页样片拼贴，对齐 imgveo 的示例区：4 列，每列 3 条，横版 16:9、竖版 9:16 都按原画幅显示。
 * 每列 1 条竖版 + 2 条横版，高度都是 2.9 倍列宽，所以四列等高、整体是个矩形；
 * 竖版在各列里错开位置，靠 config 数组顺序决定（每 3 条一列）。
 * 手机上 2 列时四个列块折成 2×2，每块仍等高。
 *
 * 视频 preload="none"，进视口才拉流并静音循环播放，离开视口就暂停；带音轨的可以点右上角开声音；
 * 页面上不放描述；悬停时显示模式、标题和 Use template 按钮，触屏设备上示例按钮常显。
 */
export function VideoShowcaseGrid({
	tiles,
	columns: columnCount = 4,
	useImagesLabel,
	soundLabels,
	mobileCarousel = false,
	equalHeightRow = false,
	singleRow = false,
	captionAtTop = false,
}: {
	tiles: ShowcaseTile[];
	columns?: 1 | 3 | 4;
	/** 「Try this example」按钮文案，已翻译；不传就不出按钮 */
	useImagesLabel?: string;
	/** 静音开关的读屏文案，已翻译；不传就不出开关 */
	soundLabels?: { unmute: string; mute: string };
	/** 手机上一行横滑（竖版样片用），只配 3 列 */
	mobileCarousel?: boolean;
	/**
	 * 每条一列排成一行，宽度按画幅比例分，横版竖版混排也等高（Burning Bridges：16:9 人像 + 9:16 宠物）。
	 * 手机上上下排；传了它 columns 和 mobileCarousel 都不起作用
	 */
	equalHeightRow?: boolean;
	/** 所有条一行排开（只配全竖版）；传了它 columns 和 mobileCarousel 都不起作用 */
	singleRow?: boolean;
	/**
	 * 成片底部烧了参考缩略图（/reference-to-video）：sm 起悬停的模式和标题挪到左上角，
	 * 按钮留在右下；手机上标题藏着、按钮常显，按钮放左上角，不然一直盖着缩略图
	 */
	captionAtTop?: boolean;
}) {
	const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());
	/** 同时只让一条出声：开一条就把其他的静掉 */
	const [soundId, setSoundId] = useState<string | null>(null);
	const toggleSound = (id: string) => {
		const next = soundId === id ? null : id;
		// muted 直接改 DOM 属性：React 对 muted 的更新不可靠。点击是用户手势，取消静音不会被拦
		videoRefs.current.forEach((video, key) => {
			video.muted = key !== next;
		});
		if (next) {
			videoRefs.current
				.get(next)
				?.play()
				.catch(() => {});
		}
		setSoundId(next);
	};

	useEffect(() => {
		const reduced = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		if (reduced) {
			return;
		}
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					const video = entry.target as HTMLVideoElement;
					if (entry.isIntersecting) {
						video.play().catch(() => {
							// 省电模式等拒绝自动播放时停在 poster
						});
					} else {
						video.pause();
					}
				}
			},
			{ threshold: 0.4 },
		);
		videoRefs.current.forEach((video) => observer.observe(video));
		return () => observer.disconnect();
	}, []);

	const perColumn = Math.ceil(tiles.length / columnCount);
	const columns =
		equalHeightRow || singleRow
			? tiles.map((tile) => [tile])
			: Array.from({ length: columnCount }, (_, index) =>
					tiles.slice(index * perColumn, (index + 1) * perColumn),
				);

	return (
		<div
			className={
				singleRow
					? SINGLE_ROW_CLASSES
					: equalHeightRow
						? EQUAL_HEIGHT_ROW_CLASSES
						: mobileCarousel
							? MOBILE_CAROUSEL_CLASSES
							: GRID_CLASSES[columnCount]
			}
		>
			{columns.map((column) => (
				<ul
					key={column[0]?.id}
					className={cn(
						"flex flex-col gap-3 sm:gap-4",
						mobileCarousel &&
							!equalHeightRow &&
							!singleRow &&
							"w-[72%] shrink-0 snap-center sm:w-auto",
						singleRow &&
							"w-[44%] shrink-0 snap-start sm:w-[30%] lg:w-auto",
						// 宽度 ∝ 宽高比时，各条高度相同
						equalHeightRow &&
							cn(
								column[0]?.orientation === "portrait"
									? "w-[62%]"
									: column[0]?.orientation === "square"
										? "w-[80%]"
										: "w-full",
								"sm:w-auto sm:[flex:var(--tile-ratio)_1_0%]",
							),
					)}
					style={
						equalHeightRow
							? ({
									"--tile-ratio":
										column[0]?.orientation === "portrait"
											? 9 / 16
											: column[0]?.orientation ===
													"square"
												? 1
												: 16 / 9,
								} as CSSProperties)
							: undefined
					}
				>
					{column.map((tile) => (
						<li
							key={tile.id}
							className="group relative overflow-hidden rounded-2xl bg-gray-4"
							style={{
								aspectRatio:
									tile.orientation === "portrait"
										? "9 / 16"
										: tile.orientation === "square"
											? "1 / 1"
											: "16 / 9",
							}}
						>
							<video
								ref={(node) => {
									if (node) {
										videoRefs.current.set(tile.id, node);
									} else {
										videoRefs.current.delete(tile.id);
									}
								}}
								src={tile.videoUrl}
								poster={tile.poster}
								aria-label={`${tile.title}. ${tile.description}`}
								preload="none"
								muted
								loop
								playsInline
								className="absolute inset-0 size-full object-cover"
							>
								<track kind="captions" />
							</video>
							{tile.hasAudio && soundLabels ? (
								<button
									type="button"
									onClick={() => toggleSound(tile.id)}
									aria-pressed={soundId === tile.id}
									aria-label={
										soundId === tile.id
											? soundLabels.mute
											: soundLabels.unmute
									}
									className={cn(
										"absolute top-3 right-3 flex size-11 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition-colors hover:bg-black/70 sm:size-9",
										// 浮层挪到顶上时会盖住它
										captionAtTop && "z-10",
									)}
								>
									{soundId === tile.id ? (
										<Volume2Icon className="size-4" />
									) : (
										<VolumeXIcon className="size-4" />
									)}
								</button>
							) : null}
							{/* 有按钮时：没有 hover 的触屏设备上浮层常显，不然按不到；键盘聚焦到按钮也显示 */}
							<figcaption
								className={cn(
									"pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 bg-gradient-to-t from-black/70 to-transparent px-3.5 pt-8 pb-3 text-white transition-opacity duration-300 focus-within:opacity-100 group-hover:opacity-100",
									"opacity-0",
									tile.alwaysShowCaption && "opacity-100",
									// 示例按钮：能 hover 的设备悬停才出；触屏没有 hover，常显
									tile.example &&
										"px-2 pt-1 pb-2 sm:px-3.5 sm:pt-8 sm:pb-3 [@media(hover:none)]:opacity-100",
									tile.inputImages &&
										useImagesLabel &&
										"[@media(hover:none)]:opacity-100",
									// 一行排开时格子窄（约 150–190px），标题和按钮并排会被按钮盖住：改成上下叠，按钮占满宽；
									// 手机 149px 宽时「Try this example」连图标放不下会折行，收窄边距、藏图标
									singleRow &&
										"flex-col items-stretch gap-2 max-sm:px-2 max-sm:pb-2",
									captionAtTop &&
										// 手机上按钮和右上角静音键同一行，藏掉图标省 20px，360 宽也不挨着
										"top-0 flex-col justify-between max-sm:items-start max-sm:bg-none max-sm:pt-3.5 max-sm:pl-3 max-sm:[&_svg]:hidden sm:bg-gradient-to-b sm:from-black/60 sm:via-transparent sm:pt-3",
								)}
							>
								{/* 手机上卡窄，标题会被按钮挤成「Hoodie…」：有按钮的卡只留按钮（同 Seedance 页） */}
								<span
									className={cn(
										"flex min-w-0 flex-col gap-0.5",
										(tile.example ||
											(tile.inputImages &&
												useImagesLabel)) &&
											"hidden sm:flex",
										// 右上角是静音开关，留出位置；flex-col 下要 self-start 才贴左
										captionAtTop &&
											"max-w-full self-start pr-12",
									)}
								>
									{tile.modeLabel ? (
										<span className="font-medium text-[11px] text-white/75 uppercase tracking-[0.06em]">
											{tile.modeLabel}
										</span>
									) : null}
									<span className="truncate font-medium text-sm">
										{tile.title}
									</span>
								</span>
								{tile.example && useImagesLabel ? (
									<UseShowcaseExampleButton
										example={tile.example}
										label={useImagesLabel}
										className="pointer-events-auto flex h-10 shrink-0 items-center gap-1.5 rounded-full bg-brand px-4 font-medium text-sm text-white shadow-cta transition-colors hover:bg-brand-500"
									/>
								) : tile.inputImages && useImagesLabel ? (
									<UseShowcaseImagesButton
										images={tile.inputImages}
										templateId={tile.templateId}
										label={useImagesLabel}
										className={cn(
											"pointer-events-auto flex h-11 shrink-0 items-center gap-1.5 rounded-full bg-brand px-3.5 font-medium text-white text-xs shadow-cta transition-colors hover:bg-brand-500 sm:h-9",
											singleRow &&
												"justify-center whitespace-nowrap px-2 max-sm:[&_svg]:hidden",
										)}
									/>
								) : null}
							</figcaption>
						</li>
					))}
				</ul>
			))}
		</div>
	);
}
